import { useMemo, useContext, createContext, useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../../redux/hooks'
import companyPaymentsActions from '../../../redux/modules/companyPayments/actions'
import companyPaymentsSelectors from '../../../redux/modules/companyPayments/selectors'
import authSelectors from '../../../redux/modules/auth/selectors'
import { useStateWithRef } from '../../../hooks/shared/useStateWithRef'
import { useUnsubscribe } from '../../../hooks/shared/useUnsubscribe'
import companyPaymentService from '../../../services/companyPaymentService'
import { CompanyPayment } from '@leadgen/models/client'
import { useRegisteredComponents } from '../../../hooks/shared/useRegisteredComponents'
import useStableCallback from '../../../hooks/shared/useStableCallback'

export type CompanyPaymentsStatus =
  | 'reloading-initial'
  | 'loading-initial'
  | 'loading-more'
  | 'idle'
  | 'empty'
  | 'error-initial'
  | 'error-more'

const CompanyPaymentsContext = createContext<{
  registerComponent: () => () => void
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadFresh: () => void
  loadMore: () => void
  status: CompanyPaymentsStatus
  companyPayments: CompanyPayment[]
  error: string | undefined
} | null>(null)

const useCompanyPayments = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(CompanyPaymentsContext)
  if (!context) {
    throw new Error('no CompanyPaymentsProvider found when calling useCompanyPayments()')
  }
  const {
    registerComponent,
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    companyPayments,
    error,
  } = context
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    companyPayments,
    error,
  }
}

const useCompanyPaymentsPreview = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(CompanyPaymentsContext)
  if (!context) {
    throw new Error('no CompanyPaymentsProvider found when calling useCompanyPayments()')
  }
  const { registerComponent, loading, status, companyPayments, error } = context
  const topThreeCompanyPayments = useMemo(() => {
    return companyPayments?.slice(0, 3)
  }, [companyPayments])
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    companyPayments: topThreeCompanyPayments,
    status,
    loading,
    error,
  }
}

const CompanyPaymentsProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const { registerComponent, hasRegisteredComponents, registeredComponentsRef } =
    useRegisteredComponents()
  const [hasMore, setHasMore, hasMoreRef] = useStateWithRef(false)
  const companyPayments = useSelector(companyPaymentsSelectors.getCompanyPayments)
  const [loading, loadingRef] = useSelectorWithRef(companyPaymentsSelectors.getLoading())
  const [loadingMore, loadingMoreRef] = useSelectorWithRef(companyPaymentsSelectors.getLoadingMore)
  const [, initialRef] = useSelectorWithRef(companyPaymentsSelectors.getInitial)
  const error = useSelector(companyPaymentsSelectors.getError)
  const companyId = useSelector(authSelectors.getCompanyId)
  const status: CompanyPaymentsStatus =
    loading && companyPayments.length
      ? 'reloading-initial'
      : loading
        ? 'loading-initial'
        : loadingMore
          ? 'loading-more'
          : !loading && !loadingMore && error && !companyPayments.length
            ? 'error-initial'
            : !loading && !loadingMore && error && companyPayments.length
              ? 'error-more'
              : !loading && !loadingMore && !error && !companyPayments.length
                ? 'empty'
                : 'idle'
  const loadFresh = useCallback(() => {
    if (
      companyId &&
      hasRegisteredComponents &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(companyPaymentsActions.loadCompanyPaymentsFresh({ companyId }))
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromCompanyPayments }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromCompanyPayments()
            unsubscribe()
          } else {
            setHasMore(data?.length === companyPaymentService.pageSize)
            unsubscribe()
            registerUnsubscribe(unsubscribeFromCompanyPayments)
          }
        })
    }
  }, [
    companyId,
    dispatch,
    hasRegisteredComponents,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  const loadMore = useCallback(() => {
    if (
      companyId &&
      hasMoreRef.current &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(companyPaymentsActions.loadCompanyPaymentsNext({ companyId }))
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromCompanyPayments }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromCompanyPayments()
            unsubscribe()
          } else {
            setHasMore(data?.length === companyPaymentService.pageSize)
            registerUnsubscribe(unsubscribeFromCompanyPayments)
          }
        })
    }
  }, [
    companyId,
    dispatch,
    hasMoreRef,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  useEffect(() => {
    loadFresh()
  }, [loadFresh])
  useEffect(() => {
    if (!hasRegisteredComponents) {
      unsubscribe()
      return
    }
  }, [hasRegisteredComponents, unsubscribe])
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
  const loadFreshHandler = useStableCallback(loadFresh)
  const loadMoreHandler = useStableCallback(loadMore)
  const value = useMemo(
    () => ({
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFresh: loadFreshHandler,
      loadMore: loadMoreHandler,
      status,
      companyPayments,
      error,
    }),
    [
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFreshHandler,
      loadMoreHandler,
      status,
      companyPayments,
      error,
    ],
  )
  return <CompanyPaymentsContext.Provider value={value}>{children}</CompanyPaymentsContext.Provider>
}

export { useCompanyPayments, useCompanyPaymentsPreview, CompanyPaymentsProvider }
export default CompanyPaymentsProvider
